import type React from 'react';
import classes from '../employees-page.module.scss';
import { EmployeeFormFields } from '../../../components/EmployeeFormFields';
import type { useEmployeesPageController } from '../useEmployeesPageController';

type EmployeeDialogsProps = Pick<
  ReturnType<typeof useEmployeesPageController>,
  | 'isCreateOpen'
  | 'canCreateEmployees'
  | 'onCloseCreateModal'
  | 'onCreateEmployee'
  | 'createForm'
  | 'setCreateForm'
  | 'sectionOptions'
  | 'createUsername'
  | 'setCreateUsername'
  | 'createPassword'
  | 'setCreatePassword'
  | 'isCreateSaving'
  | 'editingEmployeePk'
  | 'canEditEmployees'
  | 'onCloseEditModal'
  | 'isEditLoading'
  | 'editError'
  | 'editForm'
  | 'onSaveEmployeeEdit'
  | 'setEditForm'
  | 'editSectionLabel'
  | 'isEditSaving'
>;
export const EmployeeDialogs: React.FC<EmployeeDialogsProps> = ({
  isCreateOpen,
  canCreateEmployees,
  onCloseCreateModal,
  onCreateEmployee,
  createForm,
  setCreateForm,
  sectionOptions,
  createUsername,
  setCreateUsername,
  createPassword,
  setCreatePassword,
  isCreateSaving,
  editingEmployeePk,
  canEditEmployees,
  onCloseEditModal,
  isEditLoading,
  editError,
  editForm,
  onSaveEmployeeEdit,
  setEditForm,
  editSectionLabel,
  isEditSaving,
}) => (
  <>
    {isCreateOpen && canCreateEmployees && (
      <div className="modalOverlay" dir="rtl">
        <section
          className={`modalCard ${classes.employeeCreateModal}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-employee-title"
        >
          <div className="modalHeader">
            <div>
              <h2 id="create-employee-title">הוספת עובד</h2>
              <p className="homeHint">יצירת כרטיס עובד וחשבון כניסה ל-SHIFT</p>
            </div>
            <button type="button" className="modalClose" onClick={onCloseCreateModal} aria-label="סגירה">
              ×
            </button>
          </div>
          <form className="form" onSubmit={onCreateEmployee}>
            <section
              className={classes.employeeCreateSection}
              aria-labelledby="employee-personal-details-title"
            >
              <div className={classes.employeeCreateSectionHeader}>
                <div>
                  <h3 id="employee-personal-details-title">פרטים אישיים</h3>
                  <p>פרטי העובד כפי שיופיעו ברשימת הצוות ובסידור העבודה.</p>
                </div>
                <span className={classes.employeeCreateStep}>1</span>
              </div>
              <EmployeeFormFields
                form={createForm}
                onChange={(field, value) => setCreateForm((current) => ({ ...current, [field]: value }))}
                canEditSection
                sectionOptions={sectionOptions}
                sectionLabel={null}
              />
            </section>
            <section
              className={`${classes.employeeCreateSection} ${classes.employeeCredentialsBox}`}
              aria-labelledby="employee-credentials-title"
            >
              <div className={classes.employeeCreateSectionHeader}>
                <div>
                  <h3 id="employee-credentials-title">פרטי כניסה</h3>
                  <p>פרטים אלו ישמשו את העובד לכניסה למערכת SHIFT.</p>
                </div>
                <span className={classes.employeeCreateStep}>2</span>
              </div>
              <div className="formGrid">
                <div className="employeeFormField">
                  <label className="label" htmlFor="create-employee-username">
                    שם משתמש <span className="requiredMark">*</span>
                  </label>
                  <input
                    id="create-employee-username"
                    className="input"
                    dir="ltr"
                    value={createUsername}
                    onChange={(event) => setCreateUsername(event.target.value)}
                    minLength={3}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="employeeFormField">
                  <label className="label" htmlFor="create-employee-password">
                    סיסמה זמנית <span className="requiredMark">*</span>
                  </label>
                  <input
                    id="create-employee-password"
                    className="input"
                    dir="ltr"
                    type="password"
                    value={createPassword}
                    onChange={(event) => setCreatePassword(event.target.value)}
                    minLength={8}
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className={classes.employeeAccessSummary}>
                <span>הרשאה</span>
                <strong>עובד SHIFT</strong>
                <small>החשבון מאפשר כניסה למערכת המשמרות בלבד.</small>
              </div>
            </section>
            <div className="modalActions">
              <button type="button" className="navArrowButton" onClick={onCloseCreateModal}>
                ביטול
              </button>
              <button className="button" type="submit" disabled={isCreateSaving}>
                {isCreateSaving ? 'יוצר...' : 'יצירת עובד'}
              </button>
            </div>
          </form>
        </section>
      </div>
    )}
    {editingEmployeePk !== null && canEditEmployees && (
      <div className="modalOverlay" dir="rtl">
        <section className="modalCard">
          <div className="modalHeader">
            <h2>עריכת עובד</h2>
            <button type="button" className="modalClose" onClick={onCloseEditModal}>
              ×
            </button>
          </div>

          {isEditLoading && <p className="homeHint">טוען פרטי עובד...</p>}

          {editError && <p className="errorText inlineMessage">{editError}</p>}

          {!isEditLoading && editForm && (
            <form className="form" onSubmit={onSaveEmployeeEdit}>
              <EmployeeFormFields
                form={editForm}
                onChange={(field, value) => {
                  setEditForm((current) => (current ? { ...current, [field]: value } : current));
                }}
                canEditSection
                sectionOptions={sectionOptions}
                sectionLabel={editSectionLabel}
              />
              <div className={`modalActions ${classes.employeeEditActions}`}>
                <button type="button" className="navArrowButton" onClick={onCloseEditModal}>
                  ביטול
                </button>
                <button className="button" type="submit" disabled={isEditSaving}>
                  {isEditSaving ? 'שומר...' : 'שמור'}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    )}
  </>
);
